import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { lazy, Suspense, useRef, useState, type KeyboardEvent } from 'react';

import type {
  ExtractedField,
  ExtractedInvoice,
  ValidationResult,
  VatCreditStatus,
} from '@dcc-erp/shared';

import { nextIngestId } from './doc-ingest';
import { acceptPeriodGate } from '../components/doc-ingest/accept-period-guard';
import { beDateToIsoAd, buildAcceptPayload } from '../components/doc-ingest/buildAcceptPayload';
import { ConfidenceField } from '../components/doc-ingest/ConfidenceField';
import { EmailSourcePanel } from '../components/doc-ingest/EmailSourcePanel';
import { fieldLocFromExtracted, VALIDATION_FIELD_MAP } from '../components/doc-ingest/field-loc';
import { LineItemsGrid, type LineRow } from '../components/doc-ingest/LineItemsGrid';
import { MemorizedCodingChip } from '../components/doc-ingest/MemorizedCodingChip';
import { OutcomePanel } from '../components/doc-ingest/OutcomePanel';
import { RejectModal } from '../components/doc-ingest/RejectModal';
import { ReviewHeaderStrip } from '../components/doc-ingest/ReviewHeaderStrip';
import { ReviewSplit } from '../components/doc-ingest/ReviewSplit';
import { parseTermsDays } from '../components/doc-ingest/terms';
import { useAcceptReject } from '../components/doc-ingest/useAcceptReject';
import { loadDraft, useDraftAutosave } from '../components/doc-ingest/useDraftAutosave';
import { ValidationSummary } from '../components/doc-ingest/ValidationSummary';
import { VatDestinationChip } from '../components/doc-ingest/VatDestinationChip';
import { VendorMatchCard } from '../components/doc-ingest/VendorMatchCard';
import { WhtIntentFields, type WhtIntentDraft } from '../components/doc-ingest/WhtIntentFields';
import { WhyNeedsReviewPanel } from '../components/doc-ingest/WhyNeedsReviewPanel';
import { PageHead } from '../components/layout';
import { useToast } from '../components/Toast';
import { useCoa } from '../lib/queries/coa';
import { useActiveCompanyId } from '../lib/queries/companies';
import { useIngest, useIngestList, useRecheckRdVerify } from '../lib/queries/doc-ingest';
import { usePeriods } from '../lib/queries/periods';
import { useMemorizedCoding, useSupplierByTaxId } from '../lib/queries/suppliers';
import { useVendors } from '../lib/queries/vendors';

import type { DocViewerHandle } from '../components/doc-ingest/DocViewer';

// Heavy pdfjs/zoom stack must not bloat the initial SPA bundle (§12).
// DocViewer is a default export, so map m.default onto the lazy contract.
const DocViewer = lazy(() => import('../components/doc-ingest/DocViewer'));

export const Route = createFileRoute('/doc-ingest/$id')({
  component: ReviewPage,
});

function mimeFromUrl(url: string): string {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('.pdf')) return 'application/pdf';
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.webp')) return 'image/webp';
  return 'application/pdf';
}

export function ReviewPage() {
  const { id } = useParams({ from: '/doc-ingest/$id' });
  const companyId = useActiveCompanyId();
  const { data: proposal, isPending, error } = useIngest(companyId, id);

  // vatCreditStatus is editable in REVIEWING; seed from the proposal and re-seed
  // when a different ingest loads. We adjust state during render (React's
  // documented pattern for deriving state from props) keyed on the loaded
  // ingestId — no effect, so it stays clear of react-hooks/set-state-in-effect.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [vatCredit, setVatCredit] = useState<VatCreditStatus | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (proposal && seededFor !== proposal.ingestId) {
    setSeededFor(proposal.ingestId);
    setVatCredit(proposal.vatCreditStatus);
  }

  return (
    <div className="page">
      <PageHead crumbs={['ตรวจเอกสาร AI', 'ตรวจสอบเอกสาร']} title="ตรวจสอบเอกสาร (AI)" />

      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}

      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}

      {/* M5 — a deferred SCAN proposal can be at EXTRACTING with no extracted/
          validation yet (the overnight Batch API fills them later); there is
          nothing to review until it lands. getProposal 404s in that case, but
          guard defensively so the review pane only mounts on a real extraction. */}
      {!isPending && !error && proposal && (!proposal.extracted || !proposal.validation) && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          เอกสารยังไม่มีผลการอ่าน (กำลังประมวลผล)
        </div>
      )}

      {!isPending && !error && proposal?.extracted && proposal.validation && (
        <ReviewBody
          key={proposal.ingestId}
          proposal={{ ...proposal, extracted: proposal.extracted, validation: proposal.validation }}
          companyId={companyId}
          vatCredit={vatCredit}
          onVatCreditChange={setVatCredit}
        />
      )}
    </div>
  );
}

type LoadedProposal = NonNullable<ReturnType<typeof useIngest>['data']>;
interface ReviewBodyProps {
  // ReviewBody renders a loaded extraction; narrow extracted/validation to
  // non-null (the proposal schema widened them for the deferred SCAN path).
  proposal: LoadedProposal & {
    extracted: NonNullable<LoadedProposal['extracted']>;
    validation: NonNullable<LoadedProposal['validation']>;
  };
  companyId: string | undefined;
  vatCredit: VatCreditStatus | null;
  onVatCreditChange: (v: VatCreditStatus) => void;
}

// Top→bottom card order in the right pane (matches the §6 field-key scheme).
const FIELD_CARDS: { key: string; label: string }[] = [
  { key: 'docTitle', label: 'ประเภทเอกสาร' },
  { key: 'seller.name', label: 'ชื่อผู้ขาย' },
  { key: 'seller.taxId', label: 'เลขภาษีผู้ขาย' },
  { key: 'seller.branch', label: 'สาขาผู้ขาย' },
  { key: 'buyer.name', label: 'ชื่อผู้ซื้อ' },
  { key: 'buyer.taxId', label: 'เลขภาษีผู้ซื้อ' },
  { key: 'invoiceNo', label: 'เลขที่ใบกำกับ' },
  { key: 'invoiceDate', label: 'วันที่เอกสาร' },
  { key: 'subtotal', label: 'ยอดก่อน VAT' },
  { key: 'vatAmount', label: 'ภาษีมูลค่าเพิ่ม' },
  { key: 'total', label: 'ยอดรวมทั้งสิ้น' },
];

// Resolve a field-key to its extracted ExtractedField (the value/confidence the
// ConfidenceField card renders). Mirrors fieldLocFromExtracted's key scheme.
// Only the FIELD_CARDS keys are passed in, so the lookup always resolves; the
// empty fallback keeps the return type non-nullable for the card props.
const EMPTY_FIELD: ExtractedField = {
  value: null,
  confidence: 'low',
  sourceSnippet: null,
  page: null,
};
function fieldFromKey(extracted: ExtractedInvoice, key: string): ExtractedField {
  const [head, sub] = key.split('.');
  if (sub && (head === 'seller' || head === 'buyer')) {
    return extracted[head][sub as keyof ExtractedInvoice['seller']] ?? EMPTY_FIELD;
  }
  const top = (extracted as unknown as Record<string, ExtractedField | undefined>)[head ?? ''];
  return top ?? EMPTY_FIELD;
}

// Build the per-field invalid-message map from the failed validation checks via
// the SINGLE VALIDATION_FIELD_MAP source (field-loc.ts).
function buildInvalidMap(validation: ValidationResult) {
  const map: Record<string, { message: string; critical: boolean }> = {};
  for (const c of validation.checks) {
    if (c.passed) continue;
    const fieldKey = VALIDATION_FIELD_MAP[c.code];
    if (!fieldKey) continue;
    // Critical wins; otherwise keep the first message for the field.
    if (!map[fieldKey] || c.severity === 'critical') {
      map[fieldKey] = { message: c.message, critical: c.severity === 'critical' };
    }
  }
  return map;
}

const round2 = (n: number) => +n.toFixed(2);
const numOf = (s: string) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

function ReviewBody({ proposal, companyId, vatCredit, onVatCreditChange }: ReviewBodyProps) {
  const editable = proposal.status === 'REVIEWING';
  const mimeType = mimeFromUrl(proposal.docUrl);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const viewerRef = useRef<DocViewerHandle>(null);
  const focus = (fieldKey: string) =>
    viewerRef.current?.focusField(fieldKey, fieldLocFromExtracted(proposal.extracted, fieldKey));

  // --- Open periods (Accept gate) + supplier resolution (memorized coding +
  // WHT defaults) keyed off the OCR seller tax-id (§15.1).
  const { data: periods } = usePeriods(companyId);
  const sellerTaxId = proposal.extracted.seller.taxId.value;
  const supplierQ = useSupplierByTaxId(companyId, sellerTaxId);
  const supplierId = supplierQ.data?.id;
  const recheck = useRecheckRdVerify(companyId, proposal.ingestId);

  // --- COA expense/cost options + vendor options for the pickers.
  const { data: coa } = useCoa(companyId);
  const expenseOptions = (coa ?? [])
    .filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl && a.isActive)
    .map((a) => ({ value: a.code, label: `${a.code} ${a.nameTh}` }));
  const { data: vendors } = useVendors(companyId);
  const vendorOptions = (vendors ?? []).map((v) => ({
    value: v.id,
    label: v.taxId ? `${v.nameTh} (${v.taxId})` : v.nameTh,
  }));
  const vendorDefaultExpense =
    (vendors ?? []).find((v) => v.taxId === proposal.extracted.seller.taxId.value)
      ?.defaultExpenseCode ?? '';

  // --- Form state (small useState model — the prevailing doc-ingest form pattern).
  const [vendorId, setVendorId] = useState<string | null>(null);

  // The selected branch (= the transactable Vendor) carries the net-terms +
  // default department used to wire the accept payload (§15.1 fix). vendors here
  // are branch rows; vendorId is the branch id the form selected.
  const matchedBranch = (vendors ?? []).find((v) => v.id === vendorId);
  const termsDays = parseTermsDays(matchedBranch?.terms);
  const billDepartmentId = matchedBranch?.defaultDepartmentId ?? null;

  const [vendorInvoiceNo, setVendorInvoiceNo] = useState<string>(
    proposal.extracted.invoiceNo.value ?? '',
  );
  const [lines, setLines] = useState<LineRow[]>(() =>
    proposal.extracted.lineItems.map((l, i) => ({
      id: `seed-${i}`,
      description: l.description ?? '',
      qty: String(l.qty ?? 1),
      unitPrice: String(l.unitPrice ?? l.amount ?? 0),
      expenseCode: vendorDefaultExpense,
    })),
  );
  // useVendors is async, so on the first render `vendorDefaultExpense` is '' and
  // the lazy `lines` initializer above seeds every row blank. Once vendors load
  // we re-seed the vendor default into rows that are STILL blank (never clobber
  // a user edit), exactly once. Adjusting state during render (keyed on a flag)
  // is React's documented derive-from-changing-source pattern — no effect.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [defaultExpenseApplied, setDefaultExpenseApplied] = useState(false);
  if (vendors && vendorDefaultExpense && !defaultExpenseApplied) {
    setDefaultExpenseApplied(true);
    setLines((prev) =>
      prev.some((l) => !l.expenseCode)
        ? prev.map((l) => (l.expenseCode ? l : { ...l, expenseCode: vendorDefaultExpense }))
        : prev,
    );
  }
  // Provenance: fields the human Enter-confirmed.
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());

  // --- WHT intent (captured for the bill; cert is issued at PAYMENT — §15.4).
  // Seed from the matched supplier's defaults when the by-tax-id query resolves.
  const supplierDefaults = supplierQ.data;
  const [wht, setWht] = useState<WhtIntentDraft>(() => ({
    whtIncomeType: supplierDefaults?.defaultWhtIncomeType ?? null,
    whtRate:
      supplierDefaults?.defaultWhtRate != null ? String(supplierDefaults.defaultWhtRate) : '',
    whtBase: '',
  }));

  // --- Draft autosave (best-effort) + restore once on mount. Keep restore minimal
  // (vendorInvoiceNo) to avoid clobbering the lazy `lines` seed; autosave covers
  // the rest. `doAccept` closes over `clearDraft` directly (recreated each render),
  // so no ref is needed. Restore is gated by a useState flag — the codebase's
  // documented derive-from-render pattern (avoids reading a ref during render).
  const { clearDraft } = useDraftAutosave(proposal.ingestId, {
    vendorInvoiceNo,
    lines,
    vatCredit,
    wht,
  });
  const [restored, setRestored] = useState(false);
  if (!restored) {
    setRestored(true);
    const d = loadDraft<{ vendorInvoiceNo?: string }>(proposal.ingestId);
    if (d?.vendorInvoiceNo) setVendorInvoiceNo(d.vendorInvoiceNo);
  }

  // Live footing from the editable line grid.
  const subtotal = round2(lines.reduce((s, l) => s + numOf(l.qty) * numOf(l.unitPrice), 0));
  const vatAmount = round2(subtotal * 0.07);
  const total = round2(subtotal + vatAmount);

  const invalidMap = buildInvalidMap(proposal.validation);

  const ar = useAcceptReject({
    companyId,
    ingestId: proposal.ingestId,
    hasCritical: proposal.validation.hasCritical,
    vendorId,
  });

  const [rejecting, setRejecting] = useState(false);
  const matchedVendorLabel = vendorId
    ? (vendorOptions.find((o) => o.value === vendorId)?.label ?? null)
    : null;
  // The 409 problem-details body may carry the existing bill id (best-effort:
  // ApiError's typed shape doesn't include it, so read it defensively).
  const conflictBillId = ar.conflict
    ? (ar.conflict.body as { apBillId?: string }).apBillId
    : undefined;

  // Next-doc queue (REVIEWING only) for "เอกสารถัดไป".
  const { data: listData } = useIngestList(companyId, editable ? { status: 'REVIEWING' } : {});

  function onNext() {
    // nextIngestId picks the first id != current in the REVIEWING queue order.
    const order = listData?.rows.map((r) => r.ingestId) ?? [];
    const next = nextIngestId(order, proposal.ingestId);
    if (next) void navigate({ to: '/doc-ingest/$id', params: { id: next } });
    else void navigate({ to: '/doc-ingest' });
  }

  function doAccept() {
    if (!ar.canAccept || !vendorId || !vatCredit) return;
    // Open-period gate: refuse to book into a closed period (shares the journal
    // create-modal rule). Best-effort — only gate when periods + a billDate resolve.
    const billDate = beDateToIsoAd(proposal.extracted.invoiceDate.value) ?? undefined;
    const gate = periods && billDate ? acceptPeriodGate(periods, billDate) : null;
    if (gate) {
      toast.show(gate, 'err');
      return;
    }
    void ar
      .accept(
        buildAcceptPayload({
          vendorId,
          vatCreditStatus: vatCredit,
          invoiceDateRaw: proposal.extracted.invoiceDate.value,
          termsDays,
          departmentId: billDepartmentId,
          vendorInvoiceNo,
          lines,
          // WHT intent only — the WhtIntentDraft holds strings; coerce to the
          // numeric AcceptIngest fields (empty → omitted by buildAcceptPayload).
          whtIncomeType: wht.whtIncomeType,
          whtRate: wht.whtRate ? Number(wht.whtRate) : null,
          whtBase: wht.whtBase ? Number(wht.whtBase) : null,
        }),
      )
      .then((r) => {
        toast.show(r.status === 'POSTED' ? 'ลงบัญชีแล้ว' : 'ส่งอนุมัติ 4 ตา', 'ok');
        clearDraft();
      })
      .catch(() => undefined);
  }

  function onFormKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    // Ctrl/Cmd+Enter accepts (guarded); bare Enter must NOT submit/accept.
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (editable) doAccept();
    } else if (e.key === 'Enter') {
      // Never let a bare Enter inside a text input submit the form.
      const tag = (e.target as HTMLElement).tagName;
      if (tag !== 'TEXTAREA') e.preventDefault();
    }
  }

  const left = (
    <Suspense
      fallback={
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลดเอกสาร…
        </div>
      }
    >
      <DocViewer
        ref={viewerRef}
        docUrl={proposal.docUrl}
        mimeType={mimeType}
        onFieldClick={focus}
        onError={() =>
          void queryClient.invalidateQueries({
            queryKey: ['doc-ingest', companyId, proposal.ingestId],
          })
        }
      />
    </Suspense>
  );

  // OUTCOME: once accepted, replace the form with the result panel.
  const right = ar.result ? (
    <div className="review-form-shell">
      <OutcomePanel result={ar.result} onNext={onNext} />
    </div>
  ) : (
    <form
      className="review-form-shell"
      onKeyDown={onFormKeyDown}
      onSubmit={(e) => e.preventDefault()}
    >
      <ReviewHeaderStrip
        status={proposal.status}
        verifiedCount={confirmed.size}
        totalFields={FIELD_CARDS.length}
        vatCreditStatus={vatCredit}
        onVatCreditChange={onVatCreditChange}
        readOnly={!editable}
        vendorSummary={
          <span className="muted" style={{ fontSize: 13 }}>
            ผู้ขาย: {matchedVendorLabel ?? '—'}
          </span>
        }
      />

      {ar.conflict && (
        <div className="badge negative" style={{ display: 'block', padding: 10 }}>
          เอกสารนี้ซ้ำกับใบที่บันทึกไว้แล้ว{' '}
          <button
            type="button"
            className="btn sm ghost"
            onClick={() => void navigate({ to: '/ap' })}
          >
            ดูใบตั้งหนี้
            {conflictBillId ? ` (${conflictBillId})` : ''}
          </button>
        </div>
      )}

      <div className="review-form" style={{ display: 'grid', gap: 12 }}>
        {/* M4 — email-source provenance + sender-trust chips (EMAIL channel only). */}
        {proposal.emailMessage && <EmailSourcePanel email={proposal.emailMessage} />}

        <ValidationSummary validation={proposal.validation} onJump={focus} />

        {/* Pre-accept explainability: why this doc would route to 4-eyes if accepted. */}
        <WhyNeedsReviewPanel reasons={proposal.previewReasons} mode="preview" onJump={focus} />

        {FIELD_CARDS.map(({ key, label }) => {
          const field = fieldFromKey(proposal.extracted, key);
          const invalid = invalidMap[key];
          const isInvoiceNo = key === 'invoiceNo';
          return (
            <ConfidenceField
              key={key}
              fieldKey={key}
              label={label}
              field={field}
              isDirty={
                isInvoiceNo && vendorInvoiceNo !== (proposal.extracted.invoiceNo.value ?? '')
              }
              isConfirmed={confirmed.has(key)}
              invalidMessage={invalid?.message ?? null}
              isCritical={invalid?.critical ?? false}
              onReveal={focus}
              onReset={() => {
                if (isInvoiceNo) setVendorInvoiceNo(proposal.extracted.invoiceNo.value ?? '');
              }}
            >
              {isInvoiceNo && editable ? (
                <input
                  className="input"
                  aria-label={label}
                  value={vendorInvoiceNo}
                  onChange={(e) => setVendorInvoiceNo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setConfirmed((s) => new Set(s).add(key));
                    }
                  }}
                  style={{ width: '100%' }}
                />
              ) : (
                <div className="conf-field__value">{field.value ?? '—'}</div>
              )}
            </ConfidenceField>
          );
        })}

        <VendorMatchCard
          companyId={companyId}
          extracted={proposal.extracted}
          value={vendorId}
          onChange={setVendorId}
          vendorOptions={vendorOptions}
          rdVerify={proposal.rdVerify}
          onRecheck={() => void recheck.mutateAsync().catch(() => undefined)}
          recheckBusy={recheck.isPending}
        />

        {/* Per-line memorized-coding suggestions (learned from past bills for this
            supplier). One hook per line description (single-suggestion endpoint). */}
        {editable &&
          supplierId &&
          lines.map((l, i) => (
            <LineMemorizedChip
              key={`mc-${l.id}`}
              companyId={companyId}
              ingestId={proposal.ingestId}
              supplierId={supplierId}
              description={l.description}
              currentCode={l.expenseCode}
              onApply={(code) =>
                setLines((prev) =>
                  prev.map((row, j) => (j === i ? { ...row, expenseCode: code } : row)),
                )
              }
            />
          ))}

        <LineItemsGrid
          lines={lines}
          onChange={setLines}
          expenseOptions={expenseOptions}
          subtotal={subtotal}
          vatAmount={vatAmount}
          total={total}
          readOnly={!editable}
        />

        {/* Input-VAT destination + abbreviated-invoice block-claim assist. */}
        <VatDestinationChip
          status={vatCredit}
          period={(beDateToIsoAd(proposal.extracted.invoiceDate.value) ?? '').slice(0, 7)}
          isAbbreviated={(proposal.extracted.docTitle.value ?? '').includes('อย่างย่อ')}
        />

        {editable && <WhtIntentFields value={wht} onChange={setWht} />}
      </div>

      <div className="review-actionbar">
        {editable ? (
          <>
            <button
              type="button"
              className="btn ghost"
              style={{ color: 'var(--negative)' }}
              onClick={() => setRejecting(true)}
            >
              ปฏิเสธเอกสาร
            </button>
            <span style={{ flex: 1 }} />
            {ar.gateReason && (
              <span className="muted" style={{ fontSize: 13, marginRight: 8 }}>
                {ar.gateReason}
              </span>
            )}
            <button
              type="button"
              className="btn primary"
              disabled={!ar.canAccept || ar.busy}
              onClick={doAccept}
            >
              ยอมรับและลงบัญชี
            </button>
          </>
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>
            เอกสารนี้อยู่ในสถานะอ่านอย่างเดียว — ไม่สามารถแก้ไขได้
          </div>
        )}
      </div>

      {rejecting && (
        <RejectModal
          busy={ar.busy}
          error={ar.error}
          onConfirm={(reason) => {
            void ar.reject(reason).then(() => {
              setRejecting(false);
              onNext();
            });
          }}
          onClose={() => setRejecting(false)}
        />
      )}
    </form>
  );

  return <ReviewSplit left={left} right={right} />;
}

// Per-line memorized-coding chip. The shipped backend (C1.7) exposes a
// SINGLE-suggestion lookup keyed by the line description, so each line gets its
// own hook instance (Rules of Hooks: one fixed call per mounted child) rather
// than one array fetch. `MemorizedSuggestion`/`LineCodingSuggestion` share the
// same `{ expenseCode, departmentId }` shape — pass it straight through.
function LineMemorizedChip({
  companyId,
  ingestId,
  supplierId,
  description,
  currentCode,
  onApply,
}: {
  companyId: string | undefined;
  ingestId: string;
  supplierId: string;
  description: string;
  currentCode: string;
  onApply: (expenseCode: string) => void;
}) {
  const { data } = useMemorizedCoding(companyId, ingestId, supplierId, description);
  return (
    <MemorizedCodingChip
      suggestion={data ?? null}
      currentCode={currentCode}
      onApply={(s) => onApply(s.expenseCode)}
    />
  );
}
